/**
 * AI-powered Questionnaire Answer Generator
 * Analyzes client data and generates structured answers for the guided synthesis questionnaire
 */

import { stripDangerousKeys } from './sanitize.js';
import { sanitizeAIInput, hardenUserDataBlock, limitLength } from './security.js';

/**
 * Generate AI-powered answers for all questionnaire questions
 * @param {Array} questionnaireStructure - Structure of the questionnaire
 * @param {Object} clientProfile - Client profile data
 * @param {Array} works - Client diary and creative works
 * @param {Array} meetings - Clinical meetings
 * @param {Object} baseline - Baseline assessment (T0)
 * @returns {Object} Object with questionId: answer pairs
 */
export async function generateQuestionnaireAnswers(
    questionnaireStructure,
    clientProfile,
    works = [],
    meetings = [],
    baseline = null
) {
    // Prepare text sources WITH SANITIZATION
    const clientVoice = works
        .filter(w => ['diary', 'journal', 'dream'].includes(w.type))
        .map(w => {
            const content = typeof w.content === 'string' ? w.content : (w.content?.text || '');
            return sanitizeAIInput(content, 5000);
        })
        .join('\n\n');

    const counselorNotes = meetings
        .map(m => {
            const notes = m.counselorNotes || m.notes || '';
            return sanitizeAIInput(notes, 3000);
        })
        .filter(n => n.trim().length > 0)
        .join('\n\n');

    const observations = meetings
        .map(m => {
            if (!m.observations) return '';
            const { apertura, flusso, chiusura, ...rest } = m.observations;
            const legacyParts = [apertura, flusso, chiusura].filter(Boolean);
            if (legacyParts.length > 0) return sanitizeAIInput(legacyParts.join(' '), 2000);
            return sanitizeAIInput(
                Object.entries(rest)
                    .filter(([key]) => key !== 'meetingType')
                    .map(([, val]) => val)
                    .filter(Boolean)
                    .join(' '),
                2000
            );
        })
        .join(' ');

    const tags = meetings
        .map(m => sanitizeAIInput(m.counselorTags || m.tags || '', 500))
        .filter(t => t.trim().length > 0)
        .join(', ');

    // Try AI generation first
    try {
        const aiAnswers = await generateWithAI(
            questionnaireStructure,
            clientProfile,
            clientVoice,
            counselorNotes,
            observations,
            tags,
            baseline,
            meetings
        );
        return aiAnswers;
    } catch (error) {
        console.warn('AI generation failed, using heuristic fallback:', error);
        // Fallback to heuristic extraction
        return generateWithHeuristics(
            questionnaireStructure,
            clientProfile,
            clientVoice,
            counselorNotes,
            observations,
            tags,
            baseline
        );
    }
}

/**
 * Generate answers using AI (Chrome AI or Ollama)
 */
async function generateWithAI(
    questionnaireStructure,
    clientProfile,
    clientVoice,
    counselorNotes,
    observations,
    tags,
    baseline,
    meetings = []
) {
    // Build comprehensive AI prompt
    const systemPrompt = `Sei un supervisore clinico esperto in counseling BioGestalt.
Devi compilare un questionario strutturato di sintesi del cliente basandoti sui dati forniti, seguendo queste REGOLE DI FONTE:

1. INQUADRAMENTO: Sii brevissimo, una singola frase efficace.
2. DINAMICHE EMOTIVE: Attingi principalmente dalle "NOTE CLINICHE" e dalle "OSSERVAZIONI". 
   - IMPORTANTE: Sii estremamente attento al LINGUAGGIO DEL CORPO e ai segnali non verbali citati nelle note.
3. TEMI NARRATIVI: La tua fonte principale è la "MAPPA SEMANTICA" (fornita come TAG SESSIONI).
   - STILE: Usa un linguaggio più EVOCATIVO e POETICO per descrivere i temi e i nuclei narrativi.
4. PROGRESSI: Guarda la "RUOTA DELLA VITA" nella Baseline. Se i dati sono assenti o invariati, analizza l'ULTIMA NOTA CLINICA disponibile.
5. QUADRO CLINICO: Sintetizza la struttura e le indicazioni future in modo tecnico ma empatico.

REGOLE GENERALI:
- Se un dato non è disponibile, scrivi "Non emerso chiaramente dai dati".
- Mantieni un equilibrio tra precisione tecnica BioGestalt e sensibilità umana.

Rispondi SOLO con un JSON valido:
{
  "questionId": "risposta al testo",
  "questionId2": "risposta al testo"
}`;

    // Build question list for AI
    const questionsList = questionnaireStructure.flatMap(section =>
        section.questions.map(q => ({
            id: q.id,
            label: q.label,
            placeholder: q.placeholder
        }))
    );

    const safeClientName = limitLength(clientProfile.name || 'N/D', 100);
    const safeContract = sanitizeAIInput(clientProfile.contract?.title || 'N/D', 200);
    const lastMeetingNote = meetings.length > 0
        ? sanitizeAIInput(meetings[meetings.length - 1].counselorNotes || '', 3000)
        : '';

    const userPrompt = `CLIENTE: ${safeClientName}
TEMA CONTRATTUALE: ${safeContract}

=== RUOTA DELLA VITA (Baseline T0) ===
${baseline && baseline.wheelOfLife ? JSON.stringify(baseline.wheelOfLife) : 'Dati non presenti'}

${hardenUserDataBlock(tags || 'Nessun tag disponibile', 'MAPPA SEMANTICA (Tag delle sessioni)')}

${hardenUserDataBlock(clientVoice || 'Nessun contenuto', 'VOCE DEL CLIENTE (Diario e Sogni)')}

${hardenUserDataBlock(counselorNotes + '\n' + observations, 'NOTE CLINICHE E OSSERVAZIONI (Focus: Linguaggio del Corpo)')}

${hardenUserDataBlock(lastMeetingNote || 'N/D', 'ULTIMA NOTA (Da usare se Progressi/Baseline sono assenti)')}

=== DOMANDE DA COMPILARE ===
${questionsList.map(q => `${q.id}: ${q.label}`).join('\n')}

Genera ora il JSON con le risposte:`;

    // Try Chrome Built-in AI
    if (window.ai && window.ai.languageModel) {
        try {
            const capabilities = await window.ai.languageModel.capabilities();
            if (capabilities.available !== 'no') {
                const session = await window.ai.languageModel.create({
                    systemPrompt,
                    temperature: 0.3
                });
                const result = await session.prompt(userPrompt);
                session.destroy();

                // Parse JSON response
                const jsonMatch = result.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    return stripDangerousKeys(JSON.parse(jsonMatch[0]));
                }
            }
        } catch (err) {
            console.warn("Chrome AI failed:", err);
        }
    }

    // Try Local Node Proxy (Claude)
    try {
        const response = await fetch('http://127.0.0.1:3001/api/claude', { 
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                prompt: userPrompt,
                systemPrompt: systemPrompt
            })
        });

        if (response.ok) {
            const data = await response.json();
            if (data.response) {
                // Find and parse JSON within the response
                const jsonMatch = data.response.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    return stripDangerousKeys(JSON.parse(jsonMatch[0]));
                }
            }
        }
    } catch (err) {
        console.warn("Local Claude Proxy failed:", err);
    }

    throw new Error('AI_NOT_AVAILABLE');
}

/**
 * Heuristic-based answer generation (fallback)
 */
function generateWithHeuristics(
    questionnaireStructure,
    clientProfile,
    clientVoice,
    counselorNotes,
    observations,
    tags,
    baseline
) {
    const answers = {};

    // Helper: extract sentences containing keywords
    const extractSentences = (text, keywords, maxSentences = 2) => {
        if (!text) return null;
        const sentences = text.split(/[.!?]+/);
        const relevant = sentences.filter(s =>
            keywords.some(k => s.toLowerCase().includes(k.toLowerCase()))
        ).slice(0, maxSentences);
        return relevant.length > 0 ? relevant.join('. ').trim() + '.' : null;
    };

    // Helper: get first N sentences
    const getFirstSentences = (text, n = 2) => {
        if (!text) return null;
        const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 10);
        return sentences.slice(0, n).join('. ').trim() + '.';
    };

    // ANAMNESIS SECTION
    answers.age = clientProfile.age ? `${clientProfile.age} anni` : (baseline?.anamnesis?.eta?.[0]?.value ? `${baseline.anamnesis.eta[0].value} anni` : '');
    answers.profession = clientProfile.profession || baseline?.anamnesis?.lavoro?.[0]?.value || '';
    answers.motivation = baseline?.qualitative?.motivation || extractSentences(counselorNotes, ['motivazione', 'motivo', 'spinta', 'obiettivo'], 2) || '';
    answers.initialRequest = extractSentences(counselorNotes, ['richiesta', 'domanda', 'chiedere', 'tema'], 2) || clientProfile.contract?.description || '';

    // EMOTIONAL SECTION
    const emotionKeywords = ['rabbia', 'ansia', 'paura', 'tristezza', 'gioia', 'frustrazione', 'vergogna'];
    answers.dominantEmotion = extractSentences(counselorNotes + ' ' + observations, emotionKeywords, 1) || '';
    answers.emotionalPatterns = extractSentences(counselorNotes, ['pattern', 'ricorrente', 'ripete', 'sempre', 'ogni volta'], 2) || '';
    answers.emotionalBlocks = extractSentences(counselorNotes + ' ' + observations, ['blocco', 'blocca', 'ferma', 'difficoltà', 'resistenza'], 2) || '';

    // NARRATIVE SECTION
    answers.centralTheme = clientProfile.contract?.title || tags.split(',')[0]?.trim() || '';
    answers.subThemes = tags ? `Temi osservati: ${tags.split(',').slice(0, 5).join(', ')}` : '';
    answers.narrativeNuclei = extractSentences(clientVoice, ['ricordo', 'infanzia', 'sempre', 'padre', 'madre', 'famiglia'], 3) || '';

    // PROGRESS SECTION
    answers.mainInsights = extractSentences(counselorNotes, ['consapevolezza', 'insight', 'capire', 'comprendere', 'rendersi conto'], 3) || '';
    answers.growthAreas = extractSentences(counselorNotes, ['crescita', 'cambiamento', 'miglioramento', 'progresso', 'evoluzione'], 2) || '';
    answers.resistances = extractSentences(counselorNotes, ['resistenza', 'ostacolo', 'difficoltà', 'blocco', 'evita'], 2) || '';

    // CLINICAL SECTION
    const structureKeywords = ['orale', 'masochista', 'rigido', 'schizoide', 'psicopatico', 'narcisistico'];
    answers.characterStructure = extractSentences(counselorNotes, structureKeywords, 1) || '';
    answers.transferDynamics = extractSentences(counselorNotes + ' ' + observations, ['transfer', 'controtransfer', 'relazione', 'dinamica'], 2) || '';
    answers.clinicalIndications = extractSentences(counselorNotes, ['lavorare', 'focus', 'obiettivo', 'strategia', 'intervento'], 3) || '';

    return answers;
}

/**
 * Format baseline data for AI prompt
 */
function formatBaseline(baseline) {
    if (!baseline) return 'Non disponibile';

    const parts = [];

    if (baseline.anamnesis) {
        const { eta, lavoro } = baseline.anamnesis;
        if (eta?.[0]?.value) parts.push(`Età: ${eta[0].value} anni`);
        if (lavoro?.[0]?.value) parts.push(`Professione: ${lavoro[0].value}`);
    }

    if (baseline.qualitative) {
        const { motivation } = baseline.qualitative;
        if (motivation) parts.push(`Motivazione: ${motivation}`);
    }

    if (baseline.wheelOfLife && Object.keys(baseline.wheelOfLife).length > 0) {
        const wheelData = Object.entries(baseline.wheelOfLife)
            .map(([area, score]) => `${area}: ${score}`)
            .join(', ');
        parts.push(`Ruota della Vita (T0): ${wheelData}`);
    }

    return parts.length > 0 ? parts.join('\n') : 'Dati limitati';
}
